import React, { useContext, useState, useEffect } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import PreviewImage from '../../../PreviewImage';
import Button from '../../../Button';
import Spinner from '../../../Spinner';
import { getAppropriateBg } from '../../_ElementaryMultiPose/MultiPosePackageBuilder/steps/FavoritePoseSelection';
import { CustomerContext } from '../../../../contexts/CustomerContext';
import useSeniorBuyAllPromoOption from '../../../../hooks/useSeniorBuyAllPromoOption';
import useBuyAllOption from '../../../../hooks/useBuyAllOption';
import usePoses from '../../../../hooks/usePoses';
import useBackgrounds from '../../../../hooks/useBackgrounds';
import usePricelist from '../../../../hooks/usePricelist';
import usePackagePoses from '../../../../hooks/usePackagePoses';
import useLocalStorage from '../../../../hooks/useLocalStorage';
import QueryKeys from '../../../../utils/QueryKeys';
import { addBuyAllToCart } from '../../../../utils/buyAllDigital';
import styles from './index.module.sass';

const FAN_COUNT = 8;
const money = (n) => `$${Number(n).toFixed(2)}`;

// Evenly pick `count` items spread across `arr` (first & last always included).
const sampleEvenly = (arr, count) => {
  if (arr.length <= count) return arr;
  return Array.from({ length: count }, (_, k) => (
    arr[Math.round((k * (arr.length - 1)) / (count - 1))]
  ));
};

// Pick `count` poses balanced across BOTH ENDS of the list — half spread across the
// first half, half across the second half. Photographers often shoot many FORMAL
// poses, which come first; sampling the whole list evenly lets those formals crowd
// out the cap & gown shots at the END. Splitting the budget across the two halves
// keeps the last images equally represented.
const sampleFromBothEnds = (arr, count) => {
  if (arr.length <= count) return arr;
  const half = Math.floor(arr.length / 2);
  const frontN = Math.ceil(count / 2);
  return [
    ...sampleEvenly(arr.slice(0, half), frontN),
    ...sampleEvenly(arr.slice(half), count - frontN),
  ];
};

const BENEFITS = [
  'Every pose',
  'Full high-resolution',
  'Print any size',
  'Yours forever',
];

/**
 * Senior "Buy All your photos" upsell — a purely-additive order-bump shown after
 * the customer builds their print package and before the add-ons screen. Designed
 * as a conversion-focused sales page (distinct from the yearbook pose grid).
 *
 * ⚠️ PURELY ADDITIVE — never touches customer.currentOrderPackage or the built
 * print package. Accepting only ADDS a new buy-all OrderPackage via /buy_all.
 *
 * printOrderPackageId is part of the Builder→upsell interface but intentionally
 * unused here (acting on the built order would violate the additive rule).
 */
// eslint-disable-next-line no-unused-vars
export default function BuyAllPromo({ packageId, printOrderPackageId, onContinue }) {
  const { customer } = useContext(CustomerContext);
  const schoolId = customer?.currentSchool?.id;
  const [currentSeason] = useLocalStorage(`currentSeason-${customer?.currentStudent?.id}`);
  const queryClient = useQueryClient();

  const { data: promoOption, isLoading: isLoadingPromo } = useSeniorBuyAllPromoOption(packageId, schoolId);
  const { data: regularOption } = useBuyAllOption(packageId, schoolId);
  const { data: poses } = usePoses(customer?.currentStudent?.id, packageId);
  const { data: backgrounds } = useBackgrounds(packageId);
  const { data: pricelist } = usePricelist(packageId);
  const { data: packagePoses } = usePackagePoses(packageId);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isFetching = useIsFetching();

  const selectablePoses = (poses || []).filter(Boolean);
  const ready = !isFetching && !isLoadingPromo;
  // Defensive: nothing to upsell (no promo option configured, or no poses) — skip
  // the screen and continue. Done in an effect (not during render) so it can never
  // double-fire under React 18 Strict Mode.
  const shouldSkip = ready && (!promoOption || selectablePoses.length === 0);
  useEffect(() => {
    if (shouldSkip) onContinue();
  }, [shouldSkip]);

  if (!ready || shouldSkip) return null;

  const promoPrice = Number(promoOption.price);
  // Compare-at: the package's REGULAR all-digital price (not this promo), shown
  // struck through when it's higher than the promo. Falls back to no strikethrough.
  const regularPrice = regularOption
    && String(regularOption.id) !== String(promoOption.id)
    && Number(regularOption.price) > promoPrice
    ? Number(regularOption.price)
    : null;
  const savings = regularPrice ? regularPrice - promoPrice : null;
  const pct = regularPrice ? Math.round((savings / regularPrice) * 100) : null;

  const poseCount = selectablePoses.length;
  const fanPoses = sampleFromBothEnds(selectablePoses, FAN_COUNT);
  const remaining = poseCount - fanPoses.length;
  const mid = (fanPoses.length - 1) / 2;

  const accept = async () => {
    setSaving(true);
    setError(null);
    try {
      await addBuyAllToCart({
        cartId: customer.cart.id,
        packageId,
        optionId: promoOption.id,
        studentId: customer.currentStudent.id,
        season: currentSeason,
      });
      // Refresh cart-dependent views in the background; don't block navigation (the
      // downstream screens fetch what they need on mount).
      queryClient.invalidateQueries({ queryKey: [QueryKeys.cart] });
      // Continue through the add-ons chain (announcements & gifts) exactly like the
      // built package and the "All Digital" fast path — jumping straight to review
      // skipped the announcements screen, so the offer's free announcements were
      // never offered to buy-all customers (2026-07-27). The add-on sheets attach to
      // the built PRINT package (goAfterBuild's default), same as declining the promo.
      onContinue();
    } catch (err) {
      console.error('Buy All promo failed:', err);
      setError('Something went wrong adding all your photos. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.badge}>Today only</div>

      <h1 className={styles.headline}>Get every photo from your session</h1>

      <p className={styles.subhead}>
        {'Your purchase unlocked the lowest price we offer — add high-res downloads of all '}
        <strong>{poseCount}</strong>
        {' portraits.'}
      </p>

      <div className={styles.pricing}>
        {regularPrice && <span className={styles.wasPrice}>{money(regularPrice)}</span>}
        <span className={styles.nowPrice}>{money(promoPrice)}</span>
        {savings && (
          <span className={styles.saveBadge}>
            {`SAVE $${Math.round(savings)}`}
            <span className={styles.savePct}>{`${pct}% OFF`}</span>
          </span>
        )}
      </div>

      <p className={styles.availability}>Available right now because you bought a package.</p>

      <div className={styles.fan}>
        {fanPoses.map((pose, i) => {
          const bg = getAppropriateBg(packagePoses, pose, backgrounds, pricelist)
            || (backgrounds && backgrounds[i % backgrounds.length]);
          const angle = (i - mid) * 8;
          const lift = Math.abs(i - mid) * 8;

          return (
            <div
              key={pose.id}
              className={styles.fanCard}
              style={{
                transform: `rotate(${angle}deg) translateY(${lift}px)`,
                zIndex: fanPoses.length - Math.round(Math.abs(i - mid)),
              }}
            >
              <PreviewImage
                classes={styles.fanImg}
                isPreviewHidden={false}
                foregroundUrl={pose.watermarkUrl}
                backgroundUrl={bg?.imageUrl || (backgrounds && backgrounds[0]?.imageUrl)}
              />
            </div>
          );
        })}
      </div>

      {remaining > 0 && (
        <p className={styles.andMore}>{`And ${remaining} more`}</p>
      )}

      <div className={styles.benefits}>
        {BENEFITS.map((b) => (
          <div key={b} className={styles.benefit}>
            <span className={styles.check}>✓</span>
            <span>{b}</span>
          </div>
        ))}
      </div>

      <div className={styles.printRelease}>
        <span className={styles.check}>✓</span>
        <span>Includes Print Release</span>
      </div>

      <div className={styles.ctaWrap}>
        {error && <p className={styles.error}>{error}</p>}
        {saving && <Spinner color="blue" />}
        {!saving && (
          <>
            <Button bold circular fullWidth variant="primary" onClick={accept}>
              {`Add all my photos — ${money(promoPrice)}`}
            </Button>
            {/* Arrow-wrap: onContinue is Builder's goAfterBuild, whose first param is an
                optional order-package id — the raw click event must not leak into it. */}
            <button type="button" className={styles.skip} onClick={() => onContinue()}>
              No thanks, skip the digital files
            </button>
          </>
        )}
      </div>
    </div>
  );
}
